import React, { useMemo, useState } from 'react';

import type { InfiniteData } from '@tanstack/react-query';
import { Empty, Typography } from 'antd';

import type { OpenMetadataOwner, OpenMetadataResponse } from '@/shared/api/openmetadata';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useSearchLengthValidation } from '@/shared/hooks/useSearchLengthValidation';
import { EMPTY_MESSAGES, VALIDATION_MESSAGES } from '@/shared/lib/constants';
import { sanitizeToText } from '@/shared/lib/sanitize';
import { getTransformSortByToSortOption } from '@/shared/lib/sort/transformSortBy';
import { transformInfiniteDataToSearchResults } from '@/shared/lib/utils/openMetadataTransform';
import { EmptyStateMessage } from '@/shared/ui/EmptyStateMessage';
import { SearchInput } from '@/shared/ui/SearchInput';

import { type SearchResult, useSearchStore } from '@/entities/search';

import { useDataAssets } from '@/features/domain/model/useDataAssets';

import { SearchResults } from '@/widgets/search/SearchResults';

import styles from './DataAssetsTab.module.css';

const { Text } = Typography;

type DataAssetResult = SearchResult & {
  owners?: OpenMetadataOwner[];
  followers?: unknown[];
};

type DataAssetsTabProps = {
  domainFqn: string;
};

export const DataAssetsTab: React.FC<DataAssetsTabProps> = ({ domainFqn }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, 300);
  const sanitizedQuery = sanitizeToText(debouncedQuery);

  const { validateLength, handleCompositionStart, handleCompositionEnd } = useSearchLengthValidation({
    maxLength: 30,
    warningMessage: VALIDATION_MESSAGES.DOMAIN_SEARCH_MAX_LENGTH,
    debounceMs: 1000,
  });

  const emptyFilters = useMemo(
    () => ({
      dataSource: [],
      assetType: [],
      domain: [],
      owner: [],
      tier: [],
      tags: [],
      certification: [],
    }),
    []
  );

  const { sortBy } = useSearchStore();
  const transformedSort = getTransformSortByToSortOption(sortBy);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isError, error } = useDataAssets({
    domainFqn,
    filters: emptyFilters,
    query: sanitizedQuery,
    enabled: !!domainFqn,
    sortBy: transformedSort.field,
    sortOrder: transformedSort.direction,
  });

  const allResults = useMemo<DataAssetResult[]>(() => {
    return transformInfiniteDataToSearchResults<DataAssetResult>(
      data as InfiniteData<OpenMetadataResponse> | undefined,
      'auto',
      (hit) => ({
        owners: hit._source.owners,
        followers: [],
        serviceType: hit._source.serviceType,
      })
    );
  }, [data]);

  const totalCount = useMemo(() => {
    const typedData = data as InfiniteData<OpenMetadataResponse> | undefined;
    return typedData?.pages?.[0]?.hits?.total?.value || 0;
  }, [data]);

  const emptyContent = useMemo(() => {
    if (isError) {
      return (
        <div className={styles.emptyState}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="danger">{error instanceof Error ? error.message : '오류가 발생했습니다'}</Text>}
          />
        </div>
      );
    }

    if (allResults.length === 0) {
      const message = sanitizedQuery
        ? `"${sanitizedQuery}"에 대한 검색 결과가 없습니다`
        : EMPTY_MESSAGES.NO_CONNECTED_ASSETS;
      return <EmptyStateMessage message={message} />;
    }

    return null;
  }, [isError, error, allResults.length, sanitizedQuery]);

  return (
    <div className={styles.dataAssetsTab}>
      <div className={styles.contentArea}>
        <div className={styles.resultsWithPreview}>
          <div className={styles.resultsContainer}>
            <SearchResults
              results={allResults}
              query={sanitizedQuery}
              totalCount={totalCount}
              isFetching={isFetching}
              isLoading={isLoading}
              onLoadMore={() => hasNextPage && fetchNextPage()}
              hasMore={hasNextPage}
              headerRight={
                <SearchInput
                  placeholder="이 도메인 내에서 검색"
                  value={searchQuery}
                  onChange={(e) => {
                    const validatedValue = validateLength(e.target.value);
                    setSearchQuery(validatedValue);
                  }}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                  className={styles.searchInput}
                  allowClear
                  maxLength={30}
                />
              }
              emptyContent={emptyContent}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
